// src/config/guardiansCatalog.js
//
// Catalog of guardian companions that can ride along with the player's avatar
// in the metaverse.
//
// IMPORTANT: `id` is the contract with Unity. React sends `set_guardian <id>`
// over the shell command route, and Unity's Guardian_Controller looks the id up
// in its own catalog on the body-group-base prefab. These are stable string ids
// on purpose - never switch them to list indices, or reordering this array would
// silently change which guardian an existing user owns.

export const GUARDIAN_NONE_ID = 'none';

/**
 * @typedef {Object} Guardian
 * @property {string} id            Stable id, must match Unity's catalog.
 * @property {string} name          Display name.
 * @property {string} description   Short blurb for the selection tile.
 * @property {number} cost          Price in QuikCoins. 0 = free.
 * @property {boolean} [alwaysUnlocked] Never needs purchasing.
 */

/** @type {Guardian[]} */
export const GUARDIANS = [
  {
    id: GUARDIAN_NONE_ID,
    name: 'No Guardian',
    description: 'Explore on your own.',
    cost: 0,
    alwaysUnlocked: true,
  },
  // TODO(pricing): all costs are 0 for testing. Set real prices here before release.
  {id: 'g1', name: 'Nova', description: 'A bright little spark that never leaves your side.', cost: 0},
  {id: 'g2', name: 'Pixel', description: 'Curious, quick, and always scanning the room.', cost: 0},
  {id: 'g3', name: 'Bolt', description: 'Fast on the draw and full of energy.', cost: 0},
  {id: 'g4', name: 'Echo', description: 'Quiet company that repeats every good idea.', cost: 0},
  {id: 'g5', name: 'Ember', description: 'A warm glow for the long study sessions.', cost: 0},
  {id: 'g6', name: 'Comet', description: 'Leaves a trail of stardust wherever you go.', cost: 0},
  {id: 'g7', name: 'Sage', description: 'The thoughtful one. Knows when to hang back.', cost: 0},
  {id: 'g8', name: 'Zephyr', description: 'Light as air and twice as fast.', cost: 0},
  {id: 'g9', name: 'Orbit', description: 'Circles you like a tiny friendly moon.', cost: 0},
];

/** Ids that are free / owned by everyone from the start. */
export const DEFAULT_UNLOCKED_GUARDIAN_IDS = GUARDIANS.filter(
  g => g.alwaysUnlocked,
).map(g => g.id);

/** @returns {Guardian|null} */
export function getGuardianById(id) {
  if (!id) return null;
  return GUARDIANS.find(g => g.id === id) || null;
}

/** True when the id is one this build knows about. */
export function isKnownGuardianId(id) {
  return !!getGuardianById(id);
}

export default GUARDIANS;
